import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoSettingsOutline } from 'react-icons/io5';

export default function Navbar() {
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeLink, setActiveLink] = useState(location.pathname);
  const { loginWithRedirect, logout, isAuthenticated, user } = useAuth0();

  const handleNavigate = (url) => () => {
    setActiveLink(url);
    navigate(url);
    setNavbarOpen(false);
  };

  const toggleNavbar = () => setNavbarOpen((prev) => !prev);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile && navbarOpen) setNavbarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navbarOpen]);

  useEffect(() => {
    if (navbarOpen && isMobile) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [navbarOpen, isMobile]);

  const menuLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/ventas', label: 'Inmuebles' },
  ];

  return (
    <motion.nav className="bg-white fixed w-full h-32 z-40 top-0 start-0 border-b border-gray-200">
      <motion.div className="w-full h-full flex flex-wrap items-center justify-between mx-auto p-4">
        {/* Logo */}
        <motion.div className="flex items-center space-x-3 rtl:space-x-reverse">
          <img
            src="/SolNacienteLogo.png"
            className="sm:h-24 h-20"
            alt="Sol Naciente Logo"
          />
        </motion.div>

        {/* Redes sociales + login/logout */}
        <motion.div className="flex md:order-2 space-x-2 rtl:space-x-reverse">
          {/* Instagram */}
          <a
            href="https://www.instagram.com/inmobiliaria_sol_naciente"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center group transition-colors duration-500 p-2 w-11 h-11 justify-center text-sm text-gray-400 rounded-lg hover:bg-gradient-to-bl from-purple-700 via-pink-600 to-yellow-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <svg
              viewBox="0 0 30 30"
              fill="currentColor"
              className="h-8 w-8 group-hover:text-white transition-colors duration-500"
            >
              <circle cx="15" cy="15" r="4" />
              <path d="M19.999,3h-10C6.14,3,3,6.141,3,10.001v10C3,23.86,6.141,27,10.001,27h10C23.86,27,27,23.859,27,19.999v-10 C27,6.14,23.859,3,19.999,3z M15,21c-3.309,0-6-2.691-6-6s2.691-6,6-6s6,2.691,6,6S18.309,21,15,21z M22,9c-0.552,0-1-0.448-1-1 c0-0.552,0.448-1,1-1s1,0.448,1,1C23,8.552,22.552,9,22,9z" />
            </svg>
          </a>

          {/* Facebook */}
          <a
            href="https://www.facebook.com/profile.php?id=61556897639058"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center group transition-colors duration-500 p-2 w-11 h-11 justify-center text-sm text-gray-400 rounded-lg hover:bg-customBlue focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <svg
              className="h-7 w-7 group-hover:text-white transition-colors duration-500"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" />
              <path d="M7 10v4h3v7h4v-7h3l1 -4h-4v-2a1 1 0 0 1 1 -1h3v-4h-3a5 5 0 0 0 -5 5v2h-3" />
            </svg>
          </a>

          {/* Configuración (solo autenticado) */}
          {isAuthenticated && (
            <button
              type="button"
              onClick={
                activeLink === '/inmuebles'
                  ? undefined
                  : handleNavigate('/inmuebles')
              }
              className={`inline-flex items-center group transition-colors duration-500 p-2 w-11 h-11 justify-center text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200
      ${
        activeLink === '/inmuebles'
          ? 'bg-gray-200 text-gray-700 cursor-default'
          : 'text-gray-400 hover:bg-gray-200 hover:text-gray-700 cursor-pointer'
      }
    `}
              title="Configuración"
              disabled={activeLink === '/inmuebles'}
              style={
                activeLink === '/inmuebles'
                  ? { backgroundColor: '#e5e7eb', color: '#374151' }
                  : {}
              }
            >
              <IoSettingsOutline className="h-7 w-7 transition-colors duration-500" />
            </button>
          )}

          {/* Login/Logout Desktop */}
          {!isAuthenticated ? (
            <button
              type="button"
              onClick={() => loginWithRedirect()}
              className="hidden md:inline-flex items-center group transition-colors duration-500 p-2 h-11 justify-center text-sm text-gray-400 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              <svg
                className="h-7 w-7 group-hover:text-gray-600 transition-colors duration-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="ml-2 group-hover:text-gray-600 transition-colors duration-500 font-semibold font-heading">
                Iniciar sesión
              </span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => logout({ returnTo: window.location.origin })}
              className="hidden md:inline-flex items-center group transition-colors duration-500 p-2 h-11 justify-center text-sm text-gray-400 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              <img
                src={user.picture}
                alt="User Avatar"
                className="h-7 w-7 rounded-full border-slate-400 border-2 border-solid"
              />
              <span className="ml-2 group-hover:text-gray-600 transition-colors duration-500 font-semibold font-heading">
                Cerrar sesión
              </span>
            </button>
          )}

          {/* Botón hamburguesa */}
          <button
            type="button"
            onClick={toggleNavbar}
            className="inline-flex items-center p-2 w-11 h-11 justify-center text-sm text-gray-400 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
            aria-expanded={navbarOpen}
          >
            <motion.div
              className="relative w-6 h-6"
              initial={false}
              animate={navbarOpen ? 'open' : 'closed'}
              variants={{
                open: { transition: { staggerChildren: 0.05 } },
                closed: {
                  transition: { staggerChildren: 0.05, staggerDirection: -1 },
                },
              }}
            >
              <motion.span
                className="absolute left-0 top-1 w-6 h-0.5 bg-current"
                variants={{
                  closed: { rotate: 0, y: 0 },
                  open: { rotate: 45, y: 8 },
                }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              />
              <motion.span
                className="absolute left-0 top-3 w-6 h-0.5 bg-current"
                variants={{ closed: { opacity: 1 }, open: { opacity: 0 } }}
                transition={{ duration: 0.2 }}
              />
              <motion.span
                className="absolute left-0 top-5 w-6 h-0.5 bg-current"
                variants={{
                  closed: { rotate: 0, y: 0 },
                  open: { rotate: -45, y: -8 },
                }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              />
            </motion.div>
          </button>
        </motion.div>

        {/* Menú Desktop */}
        <motion.div className="hidden md:flex md:w-auto md:order-1">
          <ul className="flex flex-row md:space-x-8 font-semibold font-heading">
            {menuLinks.map(({ path, label }) => (
              <li key={path}>
                <div
                  className={`cursor-pointer relative pb-1 group ${
                    activeLink === path ||
                    (path === '/ventas' &&
                      activeLink.includes('/ventas/detalle/'))
                      ? 'text-customBlue'
                      : 'text-gray-700 hover:text-customBlue'
                  }`}
                  onClick={handleNavigate(path)}
                >
                  {label}
                  <motion.span
                    className="absolute left-0 bottom-0 h-[2px] bg-customBlue w-full origin-center"
                    initial={{ scaleX: 0 }}
                    animate={{
                      scaleX:
                        activeLink === path ||
                        (path === '/ventas' &&
                          activeLink.includes('/ventas/detalle/'))
                          ? 1
                          : 0,
                    }}
                    whileHover={{ scaleX: 1 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Menú Móvil Horizontal */}
        <AnimatePresence>
          {navbarOpen && isMobile && (
            <>
              <motion.div
                className="fixed top-32 left-0 w-full h-[calc(100vh-8rem)] backdrop-blur-sm bg-black/40 z-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={() => setNavbarOpen(false)}
              />
              <motion.div
                className="absolute top-32 left-0 w-full bg-white shadow-lg z-30 origin-top"
                initial={{ scaleY: 0, opacity: 0 }}
                animate={{ scaleY: 1, opacity: 1 }}
                exit={{ scaleY: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <ul className="flex flex-row justify-around items-center p-4 font-semibold font-heading h-20">
                  {menuLinks.map((link) => (
                    <motion.li key={link.path}>
                      <div
                        className={`relative pb-1 group cursor-pointer ${
                          activeLink === link.path ||
                          (link.path === '/ventas' &&
                            activeLink.includes('/ventas/detalle/'))
                            ? 'text-customBlue'
                            : 'text-gray-700 hover:text-customBlue'
                        }`}
                        onClick={handleNavigate(link.path)}
                      >
                        {link.label}
                        <motion.span
                          className="absolute left-0 bottom-0 h-[2px] bg-customBlue w-full origin-center"
                          initial={{ scaleX: 0 }}
                          animate={{
                            scaleX:
                              activeLink === link.path ||
                              (link.path === '/ventas' &&
                                activeLink.includes('/ventas/detalle/'))
                                ? 1
                                : 0,
                          }}
                          whileHover={{ scaleX: 1 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        />
                      </div>
                    </motion.li>
                  ))}

                  {!isAuthenticated && (
                    <motion.li>
                      <div
                        className="relative pb-1 group cursor-pointer text-gray-700 hover:text-customBlue"
                        onClick={() => {
                          setNavbarOpen(false);
                          loginWithRedirect();
                        }}
                      >
                        Iniciar sesión
                        <motion.span
                          className="absolute left-0 bottom-0 h-[2px] bg-customBlue w-full origin-center"
                          initial={{ scaleX: 0 }}
                          whileHover={{ scaleX: 1 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        />
                      </div>
                    </motion.li>
                  )}

                  {isAuthenticated && (
                    <motion.li>
                      <div
                        className="relative pb-1 group cursor-pointer text-gray-700 hover:text-customBlue"
                        onClick={() => {
                          setNavbarOpen(false);
                          logout({ returnTo: window.location.origin });
                        }}
                      >
                        Cerrar sesión
                        <motion.span
                          className="absolute left-0 bottom-0 h-[2px] bg-customBlue w-full origin-center"
                          initial={{ scaleX: 0 }}
                          whileHover={{ scaleX: 1 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        />
                      </div>
                    </motion.li>
                  )}
                </ul>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.nav>
  );
}
